/**
 * Incident Service – QMS Phase 2
 * CRUD for incident_reports.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'incident_reports';

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export class IncidentService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  buildIncidentPayload(data, userId, now, origin = 'manual') {
    const base = {
      organizationId: data.organizationId ?? null,
      date: data.date ? Timestamp.fromDate(new Date(data.date)) : now,
      type: data.type || '',
      severity: data.severity || '',
      description: data.description || '',
      patientRef: data.patientRef || null,
      standardId: data.standardId || null,
      riskId: data.riskId || null,
      protocolId: data.protocolId || null,
      origin: origin,
      sourceThresholdId: data.sourceThresholdId || null,
      sourceTaskExecutionId: data.sourceTaskExecutionId || null,
      sourceComplianceEventId: data.sourceComplianceEventId || null,
      locationId: data.locationId || null,
      buildingId: data.buildingId || null,
      floorId: data.floorId || null,
      departmentId: data.departmentId || null,
      deviationId: data.deviationId || null,
      rcaId: data.rcaId || null,
      roomId: data.roomId || null,
      bedId: data.bedId || null,
      equipmentIds: Array.isArray(data.equipmentIds) ? data.equipmentIds : (data.equipmentId ? [data.equipmentId] : []),
      executorId: data.executorId || null,
      reportedBy: data.reportedBy || userId,
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    return omitUndefined(base);
  }

  async createSystemGeneratedIncident(data, userId) {
    const now = Timestamp.now();
    const payload = this.buildIncidentPayload({ ...data, origin: 'system_generated' }, userId, now, 'system_generated');
    const ref = await addDoc(this.collectionRef, payload);
    try {
      auditService.logCreate('incident_reports', ref.id, 'System-generated (threshold breach)', {
        type: data.type,
        severity: data.severity,
        origin: 'system_generated',
        riskId: data.riskId,
        protocolId: data.protocolId
      });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return ref.id;
  }

  async createIncident(data, userId) {
    const now = Timestamp.now();
    const payload = this.buildIncidentPayload(data, userId, now, 'manual');
    const ref = await addDoc(this.collectionRef, payload);
    try {
      auditService.logCreate('incident_reports', ref.id, data.description?.slice(0, 50) || `${data.type} - ${data.severity}`, { type: data.type, severity: data.severity });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
    return ref.id;
  }

  async getIncident(incidentId) {
    const ref = doc(this.collectionRef, incidentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async getIncidentsByOrganization(organizationId, limit = 100) {
    const org = organizationId ?? null;
    let q;
    if (org != null) {
      q = query(
        this.collectionRef,
        where('organizationId', '==', org),
        orderBy('date', 'desc')
      );
    } else {
      q = query(this.collectionRef, orderBy('date', 'desc'));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.slice(0, limit).map(d => ({ id: d.id, ...d.data() }));
  }

  async updateIncident(incidentId, updates, userId) {
    const ref = doc(this.collectionRef, incidentId);
    const current = await this.getIncident(incidentId);
    const patch = omitUndefined({ ...updates, updatedAt: Timestamp.now(), updatedBy: userId });
    if (updates.date) patch.date = Timestamp.fromDate(new Date(updates.date));
    await updateDoc(ref, patch);
    try {
      auditService.logUpdate('incident_reports', incidentId, current?.description?.slice(0, 50) || incidentId, { changedFields: Object.keys(updates) });
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  async deleteIncident(incidentId) {
    const ref = doc(this.collectionRef, incidentId);
    const current = await this.getIncident(incidentId);
    await deleteDoc(ref);
    try {
      auditService.logDelete('incident_reports', incidentId, current?.description?.slice(0, 50) || incidentId, {});
    } catch (e) {
      console.warn('Audit log failed:', e);
    }
  }

  /**
   * Back-link RCA la incident (PRD RCA v1).
   */
  async linkRCA(incidentId, rcaId, userId) {
    const ref = doc(this.collectionRef, incidentId);
    await updateDoc(ref, {
      rcaId,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }
}

export const incidentService = new IncidentService();
